import React, { forwardRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FloatingPortal,
    autoUpdate,
    flip,
    size,
    useFloating,
    useInteractions,
    useListNavigation,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';
import { cx } from './classNames';
import { floatingPaperClassName } from './Popover';
import TextInput from './TextInput';

// MUI Autocomplete's listbox in the app theme: the popover paper as wide as
// the field, 8px of padding top and bottom, at most 40% of the window tall;
// 11.5px options with 16px side padding, tinted (MUI's hover overlay) while
// highlighted or under the pointer.
const LIST_CLASS_NAME = 'm-0 box-border max-h-[40vh] list-none overflow-auto px-0 py-2';
const OPTION_CLASS_NAME = 'flex min-h-0 cursor-pointer items-center justify-start overflow-hidden px-4 py-1.5 text-[11.5px] '
    + 'leading-[1.5] hover:bg-black/[0.04] dark:hover:bg-white/[0.08]';
const ACTIVE_OPTION_CLASS_NAME = 'bg-black/[0.04] dark:bg-white/[0.08]';

const defaultRenderOption = (option) => String(option);

function matchingOptions(options, inputValue, filtering) {
    const query = filtering ? String(inputValue ?? '').trim().toLocaleLowerCase() : '';
    if (!query) return options;
    return options.filter((option) => String(option ?? '').toLocaleLowerCase().includes(query));
}

/**
 * A text field with a list of suggestions (an ARIA combobox with a listbox
 * and list autocomplete). Free text is allowed: `inputValue` is what the
 * field holds and `onInputChange(text)` reports typing. The list shows the
 * `options` that contain the typed text (ignoring case and outer spaces)
 * once the user has typed, or from the start with `filterOnOpen`; it is
 * open while the field has focus and something matches.
 *
 * ArrowDown / ArrowUp move a highlight through the list, wrapping; focus
 * stays in the field, which names the highlighted option through
 * aria-activedescendant. `onHighlightChange(option | null)` reports the
 * highlight; typing clears it. Enter commits the highlighted option, else
 * the typed text, through `onCommit(value)`; a click on an option commits
 * that option (the field keeps focus while it is pressed). Escape closes
 * the list until the user types again. `onKeyDown` runs before Enter and
 * Escape are handled, and a key it prevents is left alone.
 *
 * `renderOption(option)` draws an option (its text by default);
 * `endAdornment` sits inside the field after the text; `className` styles
 * the field (see TextInput). Other props (aria-label, onFocus, onBlur,
 * autoFocus…) go to the input.
 */
const Combobox = forwardRef(function Combobox({
    inputValue = '',
    onInputChange,
    options = [],
    renderOption = defaultRenderOption,
    filterOnOpen = false,
    onCommit,
    onHighlightChange,
    onKeyDown,
    onFocus,
    onBlur,
    endAdornment = null,
    className,
    ...inputProps
}, ref) {
    const [filtering, setFiltering] = useState(filterOnOpen);
    const [focused, setFocused] = useState(false);
    const [closed, setClosed] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);
    const shown = matchingOptions(options, inputValue, filtering);
    const open = focused && !closed && shown.length > 0;
    const highlighted = open && activeIndex != null && activeIndex < shown.length ? activeIndex : null;

    const {
        refs,
        floatingStyles,
        context,
    } = useFloating({
        open,
        onOpenChange: (next) => setClosed(!next),
        placement: 'bottom-start',
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [
            flip(),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, { width: `${rects.reference.width}px` });
                },
            }),
        ],
    });
    const role = useRole(context, { role: 'listbox' });
    const listNavigation = useListNavigation(context, {
        listRef,
        activeIndex: highlighted,
        onNavigate: (index) => {
            setActiveIndex(index);
            onHighlightChange?.(index == null ? null : shown[index] ?? null);
        },
        virtual: true,
        loop: true,
        focusItemOnHover: false,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([role, listNavigation]);
    const inputRef = useMergeRefs([refs.setReference, ref]);
    const optionId = (index) => `${context.floatingId}-option-${index}`;

    const clearHighlight = () => {
        if (activeIndex == null) return;
        setActiveIndex(null);
        onHighlightChange?.(null);
    };
    const handleKeyDown = (event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === 'Enter') {
            event.preventDefault();
            onCommit?.(highlighted == null ? inputValue : String(shown[highlighted]));
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setClosed(true);
        }
    };

    return (
        <>
            <div ref={refs.setPositionReference}>
                <TextInput
                    ref={inputRef}
                    autoComplete="off"
                    className={className}
                    endAdornment={endAdornment}
                    {...getReferenceProps({
                        ...inputProps,
                        value: inputValue,
                        'aria-autocomplete': 'list',
                        'aria-activedescendant': highlighted == null ? undefined : optionId(highlighted),
                        onChange: (event) => {
                            setFiltering(true);
                            setClosed(false);
                            clearHighlight();
                            onInputChange?.(event.target.value);
                        },
                        onFocus: (event) => {
                            setFocused(true);
                            onFocus?.(event);
                        },
                        onBlur: (event) => {
                            setFocused(false);
                            onBlur?.(event);
                        },
                        onKeyDown: handleKeyDown,
                    })}
                />
            </div>
            {open && (
                <FloatingPortal>
                    <ul
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className={cx(floatingPaperClassName, LIST_CLASS_NAME)}
                        {...getFloatingProps()}
                    >
                        {shown.map((option, index) => (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${index}:${option}`}
                                ref={(node) => { listRef.current[index] = node; }}
                                id={optionId(index)}
                                role="option"
                                aria-selected={index === highlighted}
                                className={cx(OPTION_CLASS_NAME, index === highlighted && ACTIVE_OPTION_CLASS_NAME)}
                                // Keep focus (and the typed text) in the field.
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => onCommit?.(String(option))}
                            >
                                {renderOption(option)}
                            </li>
                        ))}
                    </ul>
                </FloatingPortal>
            )}
        </>
    );
});

Combobox.propTypes = {
    inputValue: PropTypes.string,
    onInputChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    renderOption: PropTypes.func,
    filterOnOpen: PropTypes.bool,
    onCommit: PropTypes.func,
    onHighlightChange: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    endAdornment: PropTypes.node,
    className: PropTypes.string,
};

export default Combobox;
